/**
 * What every password input in this directory does with its value, in one
 * place — the leaf `password-login.tsx` and `set-password.tsx` had a copy of
 * each.
 *
 * The precedent is `is-record.ts` and `focus-ownership.ts`: a dependency-free
 * named concern that two modules legitimately share, extracted DOWNWARD rather
 * than sideways into a bucket (style §3.2 · §7 bans `utils`/`helpers`/`shared`).
 *
 * What does NOT live here is each surface's error TAXONOMY. `classifyError` and
 * `messageFor` genuinely diverge — 401 is a plain arm on set-password and an
 * ambiguous one on the ceremony routes, and the copy differs with it — so
 * merging them would force a single behaviour on two surfaces that have
 * deliberately different ones. The three arms below are the part that is the
 * same on both, because they are statements about a STRING, not about an
 * exchange.
 */
import { flushSync } from 'react-dom';

import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from './password-constants';

/**
 * The arms that are a statement about the FIELD'S VALUE.
 *
 * Each surface unions its own transport arms on top of these; only these three
 * earn `aria-invalid` (WCAG 3.3.1). A 429, a dropped connection or a 500 says
 * nothing about what was typed.
 */
export const PASSWORD_FIELD_ERRORS = ['empty', 'too-short', 'too-long'] as const;

export type PasswordFieldError = (typeof PASSWORD_FIELD_ERRORS)[number];

/**
 * Narrowing predicate over a surface's wider union.
 *
 * Typed on `string` rather than on a generic so both callers pass their own
 * union unchanged — every one of them is a union of string literals, and the
 * guard tells the caller which arm it landed on.
 */
export function isPasswordFieldError(error: string): error is PasswordFieldError {
  return (PASSWORD_FIELD_ERRORS as readonly string[]).includes(error);
}

/** Local pre-flight only — the server policy is the authority (ADR-0027). */
export function validatePassword(password: string): PasswordFieldError | null {
  if (password.length === 0) return 'empty';
  if (password.length < PASSWORD_MIN_LENGTH) return 'too-short';
  if (password.length > PASSWORD_MAX_LENGTH) return 'too-long';
  return null;
}

/**
 * Compose a field's description list.
 *
 * Order matters: the standing description comes first and the situational error
 * last, because a screen reader announces them in listed order. An id is listed
 * only while there is an error to describe, so the reference can never dangle
 * (`aria-valid-attr-value`) — which is also why the id must name a PERMANENTLY
 * mounted region rather than the message node inside it.
 *
 * Takes `hasError` rather than the error itself: the helper has no business
 * knowing either surface's union, and a boolean cannot be passed the wrong one.
 */
export function describedByFor(
  describedBy: string | undefined,
  hasError: boolean,
  errorId: string,
): string {
  return [describedBy, hasError ? errorId : undefined]
    .filter((id): id is string => id !== undefined)
    .join(' ');
}

/**
 * Clear the field and COMMIT the clear before returning.
 *
 * `flushSync` is load-bearing, not decoration. Every call site either unmounts
 * the input immediately afterwards or runs in a promise continuation, and React
 * 18 auto-batches both — so the clear and the unmount land in ONE commit, and
 * that commit removes the `<input>` without ever rendering the empty value into
 * it. The plaintext then survives in the detached node's reflected `value`
 * content attribute (React assigns a controlled input's value to
 * `defaultValue`, and that reflects). Splitting the commits is what makes the
 * clear a fact rather than an intention.
 */
export function clearPasswordSync(setPassword: (next: string) => void): void {
  flushSync(() => {
    setPassword('');
  });
}
